import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Phone, Lock, Plus, Trash2, Pencil,
  Loader2, Check, Radio, Video, VideoOff, Clock,
  ChevronUp, ChevronDown, MessageSquare, Smartphone, PhoneCall,
  QrCode, X, Link2, Unlink, Search, PowerOff, Power, Router,
  Navigation, Upload, Download, AlertTriangle, RefreshCw, Layers,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import {
  fetchSiteById, updateSite, fetchSiteContacts,
  upsertSiteContact, deleteSiteContact,
  fetchDepartments, fetchMunicipalities,
  type GeoDepartment, type GeoMunicipality,
} from '@/services/accounts';
import {
  fetchUnassignedPanels, linkPanelToSite, disablePanel, enablePanel,
  fetchAlarmZones, updateAlarmZone, deleteAlarmZone, syncZonesFromPanel,
  uploadSiteFloorPlan, deleteSiteFloorPlan, getSiteFloorPlanUrl,
} from '@/services/alarms';
import {
  getAvailableCamerasForSite, linkCameraToSite, generateRegistrationCodes, getSnapshot,
  disableCamera, enableCamera, updateCameraName,
} from '@/services/cameras';
import { supabase } from '@/services/supabase';
import { GatewayTab } from '@/components/GatewayTab';
import { PlaceSearchInput } from '@/components/PlaceSearchInput';
import { DeviceIconBadge, translateDetectorType } from '@/components/DeviceIcons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PermissionGate } from '@/components/PermissionGate';
import { useGo2rtcPlayer } from '@/hooks/use-go2rtc';
import { cn } from '@/lib/utils';
import type { AlarmSiteWithContacts, SiteContact, ContactType, AlarmPanel, Camera, AlarmZone, AlarmZoneType } from '@asi/shared';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
// Center of Guatemala
const GT_CENTER = { lat: 14.6349, lng: -90.5069 };

// ── Google Places search input ────────────────────────────────────────────────
// Moved to `@/components/PlaceSearchInput` for reuse.

const CONTACT_TYPE_CONFIG: Record<ContactType, { label: string; icon: React.ReactNode; color: string }> = {
  phone:     { label: 'Teléfono', icon: <PhoneCall className="w-3 h-3" />,   color: 'bg-blue-500/10 text-blue-600' },
  mobile:    { label: 'Celular',  icon: <Smartphone className="w-3 h-3" />,   color: 'bg-green-500/10 text-green-600' },
  whatsapp:  { label: 'WhatsApp', icon: <MessageSquare className="w-3 h-3" />, color: 'bg-emerald-500/10 text-emerald-600' },
};

// Zone types shown/edited in "Zonificación". Mirrors `alarm_zone_type` enum.
const ZONE_TYPE_CONFIG: Record<AlarmZoneType, { label: string; color: string }> = {
  PANIC:     { label: 'Pánico',            color: 'bg-purple-500/10 text-purple-600' },
  FIRE:      { label: 'Incendio/Térmica',  color: 'bg-orange-500/10 text-orange-600' },
  MEDICAL:   { label: 'Médica',            color: 'bg-red-500/10 text-red-600' },
  PERIMETER: { label: 'Perimetral',        color: 'bg-blue-500/10 text-blue-600' },
  INTERIOR:  { label: 'Interior',          color: 'bg-cyan-500/10 text-cyan-600' },
  INSTANT:   { label: 'Instantánea',       color: 'bg-amber-500/10 text-amber-600' },
  DELAY:     { label: 'Con retardo',       color: 'bg-slate-500/10 text-slate-600' },
  '24HR':    { label: '24 Horas',          color: 'bg-indigo-500/10 text-indigo-600' },
  OTHER:     { label: 'Otro',              color: 'bg-gray-500/10 text-gray-500' },
};

// Heuristic: flags zones whose name clearly suggests one device family but
// whose classified type doesn't match — e.g. a panic button classified as
// FIRE, or a motion sensor classified as MEDICAL. Checked in order, first
// match wins.
const ZONE_NAME_TYPE_HINTS: { pattern: RegExp; expected: AlarmZoneType[] }[] = [
  { pattern: /p[áa]nico|panic|bot[óo]n|duress|coacci[óo]n|\bsos\b/i, expected: ['PANIC'] },
  { pattern: /humo|incendio|fuego|calor|t[ée]rmic/i, expected: ['FIRE'] },
  { pattern: /m[ée]dic|ambulancia/i, expected: ['MEDICAL'] },
  { pattern: /magn[ée]tic|\bmg\b|puerta|ventana|port[óo]n|persiana/i, expected: ['PERIMETER', 'INTERIOR'] },
  { pattern: /movimiento|pasivo|\bpir\b|\bsm\b|sensor.*90|shock/i, expected: ['INTERIOR', 'PERIMETER'] },
];
function zoneLooksMisconfigured(zone: AlarmZone): boolean {
  const hint = ZONE_NAME_TYPE_HINTS.find((h) => h.pattern.test(zone.name));
  return !!hint && !hint.expected.includes(zone.type);
}

// The panel's own arming-behavior classification (Instant/Delay/Perimeter/...),
// stored raw in `zone_type_raw` from the ISAPI sync — distinct from `type`
// (device family: PERIMETER/INTERIOR/...) shown in the main "Tipo" badge.
function translateZoneTypeRaw(raw: string | null): string | null {
  if (!raw) return null;
  const t = raw.toLowerCase();
  if (t.includes('instant')) return 'Instantánea';
  if (t.includes('delay')) return 'Con retardo';
  if (t.includes('perimeter')) return 'Perimetral';
  if (t.includes('follower')) return 'Seguidora';
  if (t.includes('interior')) return 'Interior';
  if (t.includes('24')) return '24 Horas';
  if (t.includes('fire')) return 'Incendio';
  return raw;
}

// Device icon per zone — prefers Hikvision's own `detector_type` (ground truth
// from ISAPI sync); falls back to name keywords for zones never synced yet.
// See DeviceIcons.tsx for the actual icon components + Spanish translations.

const EMPTY_CONTACT = {
  name: '',
  phone: '',
  note: '',
  contact_type: 'mobile' as ContactType,
};

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<AlarmSiteWithContacts | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contacts');
  const [editingSite, setEditingSite] = useState(false);
  const [siteForm, setSiteForm] = useState<Partial<AlarmSiteWithContacts>>({});
  const [savingSite, setSavingSite] = useState(false);
  const [siteError, setSiteError] = useState<string | null>(null);

  // Contacts state
  const [contacts, setContacts] = useState<SiteContact[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<SiteContact | null>(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [savingContact, setSavingContact] = useState(false);

  // Related data state
  const [panels, setPanels] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [recentEvents, setRecentEvents] = useState<any[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const HISTORY_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  // Panel assignment
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [availablePanels, setAvailablePanels] = useState<AlarmPanel[]>([]);
  const [loadingAssign, setLoadingAssign] = useState(false);
  const [panelSearch, setPanelSearch] = useState('');

  // Camera assignment
  const [showAssignCamera, setShowAssignCamera] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<Camera[]>([]);
  const [cameraSearch, setCameraSearch] = useState('');

  // Disable modals
  const [disableTarget, setDisableTarget] = useState<{ type: 'camera' | 'panel'; id: string; name: string } | null>(null);
  const [disableReason, setDisableReason] = useState('');
  const [disabling, setDisabling] = useState(false);

  // QR modal
  const [qrModal, setQrModal] = useState<{ code: string; name: string } | null>(null);

  // Zonificación: zones per panel (auto-synced from arc_events) + inline editor
  const [zonesByPanel, setZonesByPanel] = useState<Record<string, AlarmZone[]>>({});
  const [zonesLoading, setZonesLoading] = useState<Record<string, boolean>>({});
  const [zonesSyncing, setZonesSyncing] = useState<Record<string, boolean>>({});
  const [editingZone, setEditingZone] = useState<AlarmZone | null>(null);
  const [zoneForm, setZoneForm] = useState<{ name: string; type: AlarmZoneType }>({ name: '', type: 'OTHER' });
  const [savingZone, setSavingZone] = useState(false);

  // Croquis de instalación (floor plan) — private bucket, viewed via signed URL
  const [floorPlanUrl, setFloorPlanUrl] = useState<string | null>(null);
  const [uploadingFloorPlan, setUploadingFloorPlan] = useState(false);
  const floorPlanInputRef = useRef<HTMLInputElement>(null);

  // Names of account members who can see this site (members belong to the account, not the site)
  const [siteMembers, setSiteMembers] = useState<string[] | null>(null);

  // Geography dropdowns
  const [departments, setDepartments] = useState<GeoDepartment[]>([]);
  const [municipalities, setMunicipalities] = useState<GeoMunicipality[]>([]);

  // Activate / deactivate site (soft “delete” — keeps audit trail intact).
  // Hard delete is intentionally NOT exposed in the UI: events, tickets,
  // panels and cameras reference the site by FK and removing the row would
  // break historical reports. Toggling `is_active=false` removes the site
  // from the mobile clients’ lists (so a non‑paying customer loses access),
  // while operators on the web dashboard still see it greyed out for context.
  const [togglingActive, setTogglingActive] = useState(false);
  async function handleToggleActive() {
    if (!site || !id) return;
    const next = !site.is_active;
    if (!next) {
      const ok = window.confirm(
        `¿Desactivar el sitio “${site.name}”?\n\n` +
        '• Desaparecerá de la app móvil del cliente.\n' +
        '• Los paneles y cámaras seguirán existiendo pero el cliente no podrá verlos.\n' +
        '• Los eventos históricos NO se borran (queda trazabilidad).\n\n' +
        'Puedes reactivarlo en cualquier momento desde esta misma pantalla.',
      );
      if (!ok) return;
    }
    setTogglingActive(true);
    try {
      await updateSite(id, { is_active: next });
      await load();
    } catch (err) {
      window.alert(`No se pudo ${next ? 'activar' : 'desactivar'} el sitio: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setTogglingActive(false);
    }
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const s = await fetchSiteById(id);
      setSite(s);
      setSiteForm(s);
      setContacts(s.contacts ?? []);
      if (s.floor_plan_path) {
        getSiteFloorPlanUrl(s.floor_plan_path).then(setFloorPlanUrl).catch(() => setFloorPlanUrl(null));
      } else {
        setFloorPlanUrl(null);
      }
      // Same rule as the alarm_sites RLS policy: an active member with managed access sees the
      // site when they have no site restriction (no grants = all account sites) or a grant for it.
      if (s.account_id) {
        const { data: members } = await supabase
          .from('account_members')
          .select('has_managed_access, profile:profiles!user_id(full_name, email), member_site_grants(site_id)')
          .eq('account_id', s.account_id)
          .eq('is_active', true);
        type MemberRow = {
          has_managed_access: boolean;
          profile: { full_name: string | null; email: string | null } | null;
          member_site_grants: { site_id: string }[];
        };
        const withAccess = ((members ?? []) as unknown as MemberRow[]).filter(
          (m) => m.has_managed_access
            && (m.member_site_grants.length === 0 || m.member_site_grants.some((g) => g.site_id === id)),
        );
        setSiteMembers(withAccess.map((m) => m.profile?.full_name || m.profile?.email || 'Sin nombre'));
      } else {
        setSiteMembers([]);
      }
    } catch (err) {
      console.error('[SiteDetailPage] load error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadRelated() {
    if (!id) return;
    setLoadingRelated(true);
    try {
      const [{ data: p }, { data: c }] = await Promise.all([
        supabase.from('alarm_panels').select('id, name, model, status, is_armed, arm_mode, serial_number, is_disabled, disabled_at').eq('site_id', id),
        supabase.from('cameras').select('id, name, status, registration_code, hikvision_device_id, go2rtc_stream_id, protocol, device_manufacturer, device_model, source, device_type, channel_no, is_disabled, disabled_at').eq('site_id', id).limit(30),
      ]);
      setPanels(p ?? []);
      setCameras(c ?? []);
    } finally {
      setLoadingRelated(false);
    }
  }

  async function loadHistoryPage(page: number, pageSize: number) {
    if (!id) return;
    setLoadingHistory(true);
    try {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, count } = await supabase
        .from('arc_events')
        .select('id, cid_code, event_category, severity, occurred_at, received_at, zone_name, acknowledged_at', { count: 'exact' })
        .eq('site_id', id)
        .order('occurred_at', { ascending: false })
        .range(from, to);
      setRecentEvents(data ?? []);
      setHistoryTotal(count ?? 0);
    } finally {
      setLoadingHistory(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    setHistoryPage(0);
  }, [id, historyPageSize]);

  useEffect(() => {
    if (!id) return;
    loadHistoryPage(historyPage, historyPageSize);
  }, [id, historyPage, historyPageSize]);

  useEffect(() => {
    // Serialize initial queries to avoid concurrent auth-lock contention.
    // Each Supabase request needs the auth token lock; firing 5+ in parallel
    // (load + loadRelated×3 + fetchDepartments) can exhaust the lock queue
    // and cause a cascading deadlock where nothing resolves.
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
      await fetchDepartments().then(setDepartments).catch(() => {});
      if (cancelled) return;
      loadRelated();
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const deptId = siteForm.department_id;
    if (!deptId) { setMunicipalities([]); return; }
    fetchMunicipalities(deptId).then(setMunicipalities).catch(() => {});
  }, [siteForm.department_id]);

  // ── Zonificación: zones for every linked panel load automatically ─────────
  useEffect(() => {
    panels.forEach((p: any) => {
      if (zonesByPanel[p.id] || zonesLoading[p.id]) return;
      setZonesLoading((prev) => ({ ...prev, [p.id]: true }));
      fetchAlarmZones(p.id)
        .then((zones) => setZonesByPanel((prev) => ({ ...prev, [p.id]: zones })))
        .catch((err) => console.error('[SiteDetailPage] fetchAlarmZones error:', err))
        .finally(() => setZonesLoading((prev) => ({ ...prev, [p.id]: false })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels]);

  function openEditZone(zone: AlarmZone) {
    setEditingZone(zone);
    setZoneForm({ name: zone.name, type: zone.type });
  }

  async function handleSaveZone() {
    if (!editingZone) return;
    setSavingZone(true);
    try {
      await updateAlarmZone(editingZone.id, { name: zoneForm.name.trim(), type: zoneForm.type });
      const panelId = editingZone.panel_id;
      const zones = await fetchAlarmZones(panelId);
      setZonesByPanel((prev) => ({ ...prev, [panelId]: zones }));
      setEditingZone(null);
    } catch (err) {
      window.alert(`No se pudo guardar la zona: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSavingZone(false);
    }
  }

  /** Pulls real device type / zone type / area straight from the panel's ISAPI zone list. */
  async function handleSyncZonesFromPanel(panelId: string, serial: string) {
    setZonesSyncing((prev) => ({ ...prev, [panelId]: true }));
    try {
      const zones = await syncZonesFromPanel(panelId, serial);
      setZonesByPanel((prev) => ({ ...prev, [panelId]: zones }));
    } catch (err) {
      window.alert(`No se pudo sincronizar con el panel: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setZonesSyncing((prev) => ({ ...prev, [panelId]: false }));
    }
  }

  async function handleDeleteZone(zone: AlarmZone) {
    const ok = window.confirm(`¿Eliminar la zona "${zone.name}" de la zonificación? Esto no afecta al panel físico.`);
    if (!ok) return;
    try {
      await deleteAlarmZone(zone.id);
      setZonesByPanel((prev) => ({ ...prev, [zone.panel_id]: (prev[zone.panel_id] ?? []).filter((z) => z.id !== zone.id) }));
    } catch (err) {
      window.alert(`No se pudo eliminar la zona: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // ── Croquis de instalación ─────────────────────────────────────────────────
  async function handleFloorPlanUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !id) return;
    setUploadingFloorPlan(true);
    try {
      const path = await uploadSiteFloorPlan(id, file);
      const url = await getSiteFloorPlanUrl(path);
      setFloorPlanUrl(url);
    } catch (err) {
      window.alert(`No se pudo subir el croquis: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploadingFloorPlan(false);
    }
  }

  async function handleFloorPlanDelete() {
    if (!id || !site?.floor_plan_path) return;
    const ok = window.confirm('¿Eliminar el croquis de instalación de este sitio?');
    if (!ok) return;
    try {
      await deleteSiteFloorPlan(id, site.floor_plan_path);
      setFloorPlanUrl(null);
      setSite((prev) => (prev ? { ...prev, floor_plan_path: null } : prev));
    } catch (err) {
      window.alert(`No se pudo eliminar el croquis: ${err instanceof Error ? err.message : String(err)}`);
    }
  }



  async function handleSaveSite() {
    if (!id) return;
    setSavingSite(true);
    setSiteError(null);
    try {
      await updateSite(id, {
        name: siteForm.name,
        address: siteForm.address,
        city: siteForm.city,
        state: siteForm.state,
        clave: siteForm.clave,
        tipo: siteForm.tipo,
        ubicacion: siteForm.ubicacion,
        notes: siteForm.notes,
        department_id: siteForm.department_id ?? null,
        municipality_id: siteForm.municipality_id ?? null,
        lat: siteForm.lat ?? null,
        lng: siteForm.lng ?? null,
        arm_reminder_enabled: siteForm.arm_reminder_enabled ?? true,
        arm_reminder_message: siteForm.arm_reminder_message ?? null,
        arm_reminder_start: siteForm.arm_reminder_start ?? '06:00:00',
        arm_reminder_end: siteForm.arm_reminder_end ?? '22:00:00',
        arm_reminder_radius_m: siteForm.arm_reminder_radius_m ?? 150,
        camera_mode: (siteForm as any).camera_mode ?? 'snapshot',
      });
      setEditingSite(false);
      await load();
    } catch (err) {
      setSiteError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSavingSite(false);
    }
  }

  function openNewContact() {
    setEditingContact(null);
    setContactForm({ ...EMPTY_CONTACT, name: '', phone: '', note: '', contact_type: 'mobile' });
    setShowContactModal(true);
  }

  function openEditContact(c: SiteContact) {
    setEditingContact(c);
    setContactForm({ name: c.name, phone: c.phone, note: c.note ?? '', contact_type: c.contact_type });
    setShowContactModal(true);
  }

  async function handleSaveContact() {
    if (!id) return;
    setSavingContact(true);
    try {
      const nextOrder = editingContact
        ? editingContact.call_order
        : (contacts.length > 0 ? Math.max(...contacts.map((c) => c.call_order)) + 1 : 1);
      await upsertSiteContact({
        id: editingContact?.id,
        site_id: id,
        call_order: nextOrder,
        name: contactForm.name.trim(),
        phone: contactForm.phone.trim(),
        note: contactForm.note.trim() || undefined,
        contact_type: contactForm.contact_type,
      });
      setShowContactModal(false);
      await load();
    } finally {
      setSavingContact(false);
    }
  }

  async function handleDeleteContact(contactId: string) {
    await deleteSiteContact(contactId);
    await load();
  }

  async function moveContact(contact: SiteContact, direction: 'up' | 'down') {
    const idx = contacts.findIndex((c) => c.id === contact.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= contacts.length) return;
    const other = contacts[swapIdx];
    // swap call_order values
    await Promise.all([
      upsertSiteContact({ ...contact, call_order: other.call_order }),
      upsertSiteContact({ ...other, call_order: contact.call_order }),
    ]);
    await load();
  }

  // ── Panel assignment ──
  async function handleOpenAssignPanel() {
    setLoadingAssign(true);
    setShowAssignPanel(true);
    try {
      const list = await fetchUnassignedPanels();
      setAvailablePanels(list);
    } catch {
      setAvailablePanels([]);
    } finally {
      setLoadingAssign(false);
    }
  }

  async function handleAssignPanel(panelId: string) {
    if (!id) return;
    await linkPanelToSite(panelId, id);
    setShowAssignPanel(false);
    await loadRelated();
  }

  async function handleUnlinkPanel(panelId: string) {
    await linkPanelToSite(panelId, null);
    await loadRelated();
  }

  // ── Camera assignment ──
  async function handleOpenAssignCamera() {    setLoadingAssign(true);
    setShowAssignCamera(true);
    try {
      const list = await getAvailableCamerasForSite(id);
      setAvailableCameras(list);
    } catch {
      setAvailableCameras([]);
    } finally {
      setLoadingAssign(false);
    }
  }

  async function handleAssignCamera(cameraId: string) {
    if (!id) return;
    await linkCameraToSite(cameraId, id);
    setShowAssignCamera(false);
    await loadRelated();
  }

  async function handleUnlinkCamera(cameraId: string) {
    await linkCameraToSite(cameraId, null);
    await loadRelated();
  }

  async function handleShowCameraQR(cam: { id: string; name: string; registration_code: string | null }) {
    if (cam.registration_code) {
      setQrModal({ code: cam.registration_code, name: cam.name });
    } else {
      const results = await generateRegistrationCodes([cam.id]);
      if (results.length > 0) {
        const newCode = results[0].registrationCode;
        setQrModal({ code: newCode, name: cam.name });
        setCameras((prev) => prev.map((c) => c.id === cam.id ? { ...c, registration_code: newCode } : c));
      }
    }
  }

  // ── Device disable / enable ──
  async function handleConfirmDisable() {
    if (!disableTarget || !site) return;
    setDisabling(true);
    try {
      const opts = {
        reason: disableReason.trim() || undefined,
        siteId: site.id,
        accountId: site.account?.id ?? null,
        deviceName: disableTarget.name,
      };
      if (disableTarget.type === 'camera') {
        await disableCamera(disableTarget.id, opts);
      } else {
        await disablePanel(disableTarget.id, opts);
      }
      setDisableTarget(null);
      setDisableReason('');
      await loadRelated();
    } finally {
      setDisabling(false);
    }
  }

  async function handleEnableDevice(type: 'camera' | 'panel', id: string, name: string) {
    if (!site) return;
    const opts = { deviceName: name, siteId: site.id, accountId: site.account?.id ?? null };
    if (type === 'camera') {
      await enableCamera(id, opts);
    } else {
      await enablePanel(id, opts);
    }
    await loadRelated();
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!site) {
    return <div className="p-6 text-muted-foreground">Sitio no encontrado.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header — "Señal de Cuenta" style */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-foreground">{site.name}</h1>
            {site.account_number && (
              <Badge variant="outline" className="font-mono text-xs">{site.account_number}</Badge>
            )}
            <Badge className={cn('text-xs', site.is_active ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-500')}>
              {site.is_active ? 'Activo' : 'Inactivo'}
            </Badge>
            {siteMembers !== null && (
              <Badge
                variant="outline"
                className="text-xs cursor-help"
                title={
                  'Miembros de la cuenta con acceso a este sitio (los miembros se afilian a la cuenta; '
                  + 'pueden ver todos sus sitios o solo los que se les asignen).'
                  + (siteMembers.length ? `\n\n${siteMembers.join('\n')}` : '')
                }
              >
                {siteMembers.length} {siteMembers.length === 1 ? 'miembro con acceso' : 'miembros con acceso'}
              </Badge>
            )}
            <Badge
              variant="outline"
              className={cn('text-xs', (site as any).camera_mode === 'livestream' ? 'bg-green-500/10 text-green-600 border-green-500/30' : 'bg-blue-500/10 text-blue-600 border-blue-500/30')}
              title="Modo de cámaras configurado para este sitio (ver/cambiar en Editar)"
            >
              {(site as any).camera_mode === 'livestream' ? '🟢 Cámaras: en vivo' : '📷 Cámaras: snapshot 5s'}
            </Badge>
          </div>
          {site.account && (
            <p className="text-xs text-muted-foreground mt-0.5">{site.account.name}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
            {site.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{site.address}{site.city ? `, ${site.city}` : ''}</span>}
            {site.tipo && <span className="px-2 py-0.5 rounded-full bg-muted font-medium">{site.tipo}</span>}
            {site.ubicacion && <span className="italic">{site.ubicacion}</span>}
          </div>
        </div>
        <PermissionGate require="alarms.manage">
          <div className="flex items-center gap-2">
            <Button
              variant={site.is_active ? 'outline' : 'default'}
              size="sm"
              onClick={handleToggleActive}
              disabled={togglingActive}
              className={site.is_active ? 'text-amber-600 hover:text-amber-700 border-amber-500/40 hover:bg-amber-500/10' : 'bg-green-600 hover:bg-green-700 text-white'}
              title={site.is_active ? 'Desactivar sitio (oculto para el cliente)' : 'Reactivar sitio'}
            >
              {togglingActive ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : site.is_active ? (
                <PowerOff className="w-4 h-4 mr-1" />
              ) : (
                <Power className="w-4 h-4 mr-1" />
              )}
              {site.is_active ? 'Desactivar' : 'Reactivar'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setEditingSite(true); setActiveTab('info'); }}>
              <Pencil className="w-4 h-4 mr-1" />
              Editar
            </Button>
          </div>
        </PermissionGate>
      </div>

      {/* Clave verbal — visible solo para operadores */}
      <PermissionGate require="alarms.manage">
        {site.clave && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/5">
            <Lock className="w-4 h-4 text-amber-500 shrink-0" />
            <div>
              <p className="text-xs text-amber-600 font-medium">Clave de verificación</p>
              <p className="text-sm font-bold text-amber-700 dark:text-amber-400 tracking-widest uppercase">{site.clave}</p>
            </div>
          </div>
        )}
      </PermissionGate>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="contacts">
            Teléfonos <Badge variant="secondary" className="ml-1">{contacts.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="panels">
            Paneles <Badge variant="secondary" className="ml-1">{panels.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="cameras">
            Cámaras <Badge variant="secondary" className="ml-1">{cameras.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
          <TabsTrigger value="info">Datos</TabsTrigger>
          <TabsTrigger value="gateway">
            <Router className="w-3.5 h-3.5 mr-1" />
            Gateway
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Teléfonos / Contactos ── */}
        <TabsContent value="contacts" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <PermissionGate require="alarms.manage">
              <Button size="sm" onClick={openNewContact}>
                <Plus className="w-4 h-4 mr-1" />
                Agregar contacto
              </Button>
            </PermissionGate>
          </div>

          {contacts.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Phone className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                <p className="text-sm text-muted-foreground">Sin contactos registrados.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {contacts.map((contact, idx) => {
                const typeCfg = CONTACT_TYPE_CONFIG[contact.contact_type];
                return (
                  <div
                    key={contact.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-card"
                  >
                    {/* Priority badge */}
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                      {contact.call_order}
                    </div>
                    {/* Move buttons */}
                    <PermissionGate require="alarms.manage">
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => moveContact(contact, 'up')}
                          disabled={idx === 0}
                          className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                        >
                          <ChevronUp className="w-3 h-3 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => moveContact(contact, 'down')}
                          disabled={idx === contacts.length - 1}
                          className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                        >
                          <ChevronDown className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </div>
                    </PermissionGate>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{contact.name}</span>
                        <Badge className={cn('text-xs flex items-center gap-1', typeCfg.color)}>
                          {typeCfg.icon}{typeCfg.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-primary font-mono mt-0.5">{contact.phone}</p>
                      {contact.note && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 italic">{contact.note}</p>
                      )}
                    </div>
                    <PermissionGate require="alarms.manage">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditContact(contact)}>
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteContact(contact.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </PermissionGate>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Paneles ── */}
        <TabsContent value="panels" className="mt-4 space-y-3">
          {/* Top toolbar: panel list with quick-access actions */}
          <div className="flex justify-end">
            <PermissionGate require="alarms.manage">
              <Button size="sm" onClick={handleOpenAssignPanel}>
                <Link2 className="w-4 h-4 mr-1" />
                Vincular panel
              </Button>
            </PermissionGate>
          </div>

          {loadingRelated ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : panels.length === 0 ? (
            <Card><CardContent className="py-10 text-center">
              <Radio className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">Sin paneles vinculados a este sitio.</p>
            </CardContent></Card>
          ) : (
            <>
              <div className="space-y-2">
                {panels.map((p: any) => (
                  <div key={p.id} className={cn('flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-card', p.is_disabled && 'opacity-60')}>
                    <Radio className="w-5 h-5 text-muted-foreground shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-foreground text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.model} · {p.serial_number}</p>
                    </div>
                                        <div className="flex items-center gap-2">
                      {p.is_disabled ? (
                        <Badge className="text-xs bg-red-500/10 text-red-600">Desactivado</Badge>
                      ) : (
                        <Badge className={cn('text-xs', p.status === 'ONLINE' ? 'bg-green-500/10 text-green-600' : p.status === 'ALARM' ? 'bg-red-500/10 text-red-600' : 'bg-gray-500/10 text-gray-500')}>
                          {p.status}
                        </Badge>
                      )}
                      {p.is_armed && !p.is_disabled && (
                        <Badge className="text-xs bg-amber-500/10 text-amber-600">
                          {p.arm_mode ?? 'Armado'}
                        </Badge>
                      )}
                      <PermissionGate require="alarms.manage">
                        {p.is_disabled ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700"
                            title="Reactivar panel"
                            onClick={() => handleEnableDevice('panel', p.id, p.name)}
                          >
                            <Power className="w-3.5 h-3.5" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-orange-500 hover:text-orange-600"
                            title="Desactivar panel"
                            onClick={() => { setDisableTarget({ type: 'panel', id: p.id, name: p.name }); setDisableReason(''); }}
                          >
                            <PowerOff className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          title="Desvincular panel"
                          onClick={() => handleUnlinkPanel(p.id)}
                        >
                          <Unlink className="w-3.5 h-3.5" />
                        </Button>
                      </PermissionGate>
                    </div>
                  </div>
                ))}
              </div>

              {/* Croquis + Zonificación — two-column layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Column 1: Croquis de instalación */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Croquis de instalación
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-3">
                      Plano de cómo quedó la instalación (ubicación de sensores, paneles y cámaras).
                      Útil para que el técnico ubique de inmediato un dispositivo a mantener o reparar.
                    </p>
                    {floorPlanUrl ? (
                      <div className="space-y-2">
                        <a href={floorPlanUrl} target="_blank" rel="noreferrer" className="block rounded-lg border border-border overflow-hidden">
                          <img src={floorPlanUrl} alt="Croquis de instalación" className="w-full h-auto object-contain bg-muted" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        </a>
                        <div className="flex gap-2">
                          <PermissionGate require="alarms.manage">
                            <Button size="sm" variant="outline" onClick={() => floorPlanInputRef.current?.click()} disabled={uploadingFloorPlan}>
                              {uploadingFloorPlan ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                              Reemplazar
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={handleFloorPlanDelete}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                            </Button>
                          </PermissionGate>
                          <a href={floorPlanUrl} download target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost"><Download className="w-3.5 h-3.5 mr-1" /> Descargar</Button>
                          </a>
                        </div>
                      </div>
                    ) : (
                      <PermissionGate require="alarms.manage" fallback={<p className="text-sm text-muted-foreground">Sin croquis subido.</p>}>
                        <Button size="sm" variant="outline" onClick={() => floorPlanInputRef.current?.click()} disabled={uploadingFloorPlan}>
                          {uploadingFloorPlan ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                          Subir croquis
                        </Button>
                      </PermissionGate>
                    )}
                    <input
                      ref={floorPlanInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                      onChange={handleFloorPlanUpload}
                    />
                  </CardContent>
                </Card>

                {/* Column 2: Zonificación (one table per linked panel, grouped by área/partición) */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Router className="w-4 h-4" /> Zonificación
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {panels.map((p: any) => {
                      const zones = zonesByPanel[p.id] ?? [];
                      // Group by real area/partition from ISAPI sync (subsystem_no).
                      // NOT arc_events.partition_no — unreliable on some multi-area AX PRO panels.
                      // `globalThis.Map` because `Map` is shadowed by the @vis.gl/react-google-maps import above.
                      const groups = new globalThis.Map<string, AlarmZone[]>();
                      for (const z of zones) {
                        const key = z.subsystem_no != null ? String(z.subsystem_no) : 'unsynced';
                        if (!groups.has(key)) groups.set(key, []);
                        groups.get(key)!.push(z);
                      }
                      const subsystemName = (subNo: number) =>
                        (p.subsystems as { id: number; name?: string }[] | undefined)?.find((s) => s.id === subNo)?.name
                        ?? `Área ${subNo}`;

                      return (
                        <div key={p.id} className={cn(panels.length > 1 && 'rounded-lg border border-border bg-muted/20 p-3')}>
                          <div className="flex items-center justify-between mb-1.5">
                            {panels.length > 1 ? (
                              <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                <Radio className="w-3.5 h-3.5 text-muted-foreground" />
                                {p.name} <span className="font-normal text-muted-foreground">· {p.serial_number}</span>
                              </p>
                            ) : <span />}
                            <PermissionGate require="alarms.manage">
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Sincronizar tipo de dispositivo y área reales desde el panel"
                                onClick={() => handleSyncZonesFromPanel(p.id, p.serial_number)}
                                disabled={zonesSyncing[p.id]}
                              >
                                {zonesSyncing[p.id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                                Sincronizar con panel
                              </Button>
                            </PermissionGate>
                          </div>

                          {zonesLoading[p.id] ? (
                            <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                          ) : zones.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">
                              Aún no se han recibido eventos de zonas de este panel. Las zonas aparecen automáticamente
                              en cuanto Hikvision reporta el primer evento, o usa "Sincronizar con panel" para traerlas de inmediato.
                            </p>
                          ) : (
                            [...groups.entries()]
                              .sort(([a], [b]) => (a === 'unsynced' ? 1 : b === 'unsynced' ? -1 : Number(a) - Number(b)))
                              .map(([key, groupZones]) => (
                                <div key={key} className="mb-3 last:mb-0">
                                  {groups.size > 1 && (
                                    <p className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground mb-1">
                                      <Layers className="w-3 h-3" />
                                      {key === 'unsynced' ? 'Área sin sincronizar' : subsystemName(Number(key))}
                                    </p>
                                  )}
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs table-fixed">
                                      <colgroup>
                                        <col className="w-[10%]" />
                                        <col className="w-[35%]" />
                                        <col className="w-[20%]" />
                                        <col className="w-[25%]" />
                                        <col className="w-[10%]" />
                                      </colgroup>
                                      <thead>
                                        <tr className="text-left text-muted-foreground border-b border-border">
                                          <th className="py-1.5 pr-3">Zona</th>
                                          <th className="py-1.5 pr-3">Dispositivo</th>
                                          <th className="py-1.5 pr-3">Tipo</th>
                                          <th className="py-1.5 pr-3">Último evento</th>
                                          <th className="py-1.5 pr-3"></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {groupZones.map((z) => {
                                          const misconfigured = zoneLooksMisconfigured(z);
                                          const armingBehavior = translateZoneTypeRaw(z.zone_type_raw);
                                          return (
                                            <tr key={z.id} className={cn('border-b border-border/50 last:border-0', z.removed_from_panel && 'opacity-50')}>
                                              {/* zone_number is 0-based, as reported directly by the panel/HPP */}
                                              <td className="py-1.5 pr-3 font-mono text-foreground truncate">{z.zone_number}</td>
                                              <td className="py-1.5 pr-3 text-foreground truncate">
                                                <div className="flex items-center gap-2">
                                                  <DeviceIconBadge detectorType={z.detector_type} name={z.name} zoneType={z.type} />
                                                  <div className="min-w-0">
                                                    <div className="truncate">
                                                      {z.name}
                                                      {misconfigured && (
                                                        <span title="El nombre del dispositivo no coincide con el tipo detectado — revisa el tipo de zona configurado en el panel." className="inline-flex items-center gap-1 ml-2 text-amber-600">
                                                          <AlertTriangle className="w-3 h-3" /> revisar
                                                        </span>
                                                      )}
                                                      {z.removed_from_panel && (
                                                        <Badge className="ml-2 text-[10px] bg-red-500/10 text-red-600" title="Este dispositivo estuvo conectado al panel y ya no aparece — probablemente fue removido físicamente.">Eliminado del panel</Badge>
                                                      )}
                                                    </div>
                                                    {z.detector_type && (
                                                      <div className="text-[10px] text-muted-foreground truncate">{translateDetectorType(z.detector_type)}</div>
                                                    )}
                                                  </div>
                                                </div>
                                              </td>
                                              <td className="py-1.5 pr-3">
                                                <Badge className={cn('text-[10px]', ZONE_TYPE_CONFIG[z.type]?.color)}>
                                                  {ZONE_TYPE_CONFIG[z.type]?.label ?? z.type}
                                                </Badge>
                                                {armingBehavior && (
                                                  <div className="text-[10px] text-muted-foreground mt-0.5" title="Comportamiento de armado configurado en el panel">
                                                    {armingBehavior}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="py-1.5 pr-3 text-muted-foreground truncate">
                                                {z.last_triggered_at ? new Date(z.last_triggered_at).toLocaleString('es-GT') : '—'}
                                                {z.last_cid_code && <span className="ml-1 font-mono">(CID {z.last_cid_code})</span>}
                                              </td>
                                              <td className="py-1.5 pr-3 text-right whitespace-nowrap">
                                                <PermissionGate require="alarms.manage">
                                                  <Button variant="ghost" size="sm" onClick={() => openEditZone(z)}>
                                                    <Pencil className="w-3 h-3" />
                                                  </Button>
                                                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteZone(z)}>
                                                    <Trash2 className="w-3 h-3" />
                                                  </Button>
                                                </PermissionGate>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>


        {/* ── Tab: Cámaras ── */}
        <TabsContent value="cameras" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <PermissionGate require="cameras.view">
              <Button size="sm" onClick={handleOpenAssignCamera}>
                <Link2 className="w-4 h-4 mr-1" />
                Asignar cámara
              </Button>
            </PermissionGate>
          </div>
          {loadingRelated ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : cameras.length === 0 ? (
            <Card><CardContent className="py-10 text-center">
              <Video className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">Sin cámaras vinculadas a este sitio.</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cameras.map((cam: any) => (
                <SiteCameraCard
                  key={cam.id}
                  cam={cam}
                  onQR={() => handleShowCameraQR(cam)}
                  onUnlink={() => handleUnlinkCamera(cam.id)}
                  onDisable={() => { setDisableTarget({ type: 'camera', id: cam.id, name: cam.name }); setDisableReason(''); }}
                  onEnable={() => handleEnableDevice('camera', cam.id, cam.name)}
                  onRenamed={(newName) => setCameras((prev: any[]) => prev.map((c: any) => c.id === cam.id ? { ...c, name: newName } : c))}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Historial ── */}
        <TabsContent value="history" className="mt-4">
          {loadingHistory ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : recentEvents.length === 0 ? (
            <Card><CardContent className="py-10 text-center">
              <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
              <p className="text-sm text-muted-foreground">Sin eventos recientes.</p>
            </CardContent></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="divide-y divide-border">
                {recentEvents.map((ev: any) => {
                  const sevCfg =
                    ev.severity === 'CRITICAL' ? { dot: 'bg-red-500', badge: 'bg-red-500/10 text-red-600' } :
                    ev.severity === 'ALARM'    ? { dot: 'bg-orange-500', badge: 'bg-orange-500/10 text-orange-600' } :
                    ev.severity === 'WARNING'  ? { dot: 'bg-yellow-500', badge: 'bg-yellow-500/10 text-yellow-600' } :
                                                 { dot: 'bg-blue-500', badge: 'bg-blue-500/10 text-blue-600' };
                  return (
                    <div key={ev.id} className="flex items-start gap-3 px-5 py-3 hover:bg-muted/40 transition-colors">
                      <div className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', sevCfg.dot)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground font-medium truncate">
                          {ev.zone_name ?? `Código ${ev.cid_code}`}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                          <Clock className="w-3 h-3 shrink-0" />
                          {new Date(ev.occurred_at).toLocaleString('es-GT', { timeZone: 'America/Guatemala' })}
                          {ev.acknowledged_at && <span className="text-green-500 font-medium">✓ Procesado</span>}
                        </p>
                      </div>
                      <Badge className={cn('text-xs shrink-0 font-mono', sevCfg.badge)}>{ev.cid_code}</Badge>
                    </div>
                  );
                })}
              </div>
              {/* ── Pagination footer ── */}
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-border bg-muted/20 flex-wrap">
                <div className="flex items-center gap-3">
                  <p className="text-xs text-muted-foreground">
                    {historyTotal === 0 ? '0 eventos' : (
                      <>
                        {historyPage * historyPageSize + 1}–{Math.min((historyPage + 1) * historyPageSize, historyTotal)} de {historyTotal}
                      </>
                    )}
                  </p>
                  <Select value={String(historyPageSize)} onValueChange={(v) => setHistoryPageSize(Number(v))}>
                    <SelectTrigger className="h-7 w-[112px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HISTORY_PAGE_SIZE_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} / página</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={historyPage === 0}
                    onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground px-1">
                    Página {historyPage + 1} de {Math.max(1, Math.ceil(historyTotal / historyPageSize))}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(historyPage + 1) * historyPageSize >= historyTotal}
                    onClick={() => setHistoryPage((p) => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>


        {/* ── Tab: Datos ── */}
        <TabsContent value="info" className="mt-4">
          {!editingSite ? (
            <div className="grid gap-4 lg:grid-cols-5">
              {/* Información del sitio */}
              <Card className="lg:col-span-3">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold">Información del sitio</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                    <InfoField label="Nombre" value={site.name} />
                    <InfoField
                      label="Cliente"
                      value={site.account ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/accounts/${site.account!.id}`)}
                          className="text-left text-primary hover:underline"
                        >
                          {site.account.name}
                        </button>
                      ) : null}
                    />
                    <InfoField label="Tipo de sitio" value={site.tipo} />
                    <InfoField label="Dirección" value={site.address} wide />
                    <InfoField
                      label="Departamento"
                      value={departments.find((d) => d.id === site.department_id)?.name ?? site.state}
                    />
                    <InfoField
                      label="Municipio"
                      value={municipalities.find((m) => m.id === site.municipality_id)?.name ?? site.city}
                    />
                    <InfoField label="Ubicación (referencia)" value={site.ubicacion} wide />
                    {site.hpp_site_id && <InfoField label="Código HPP (Hik-Partner Pro)" value={site.hpp_site_id} mono />}
                    <InfoField
                      label="Modo de cámaras"
                      value={(site as any).camera_mode === 'livestream' ? '🟢 Video en vivo' : '📷 Snapshots cada 5s'}
                    />
                    <InfoField label="Notas" value={site.notes} wide multiline />
                    <InfoField label="Creado" value={formatDateTime(site.created_at)} />
                    <InfoField label="Última actualización" value={formatDateTime(site.updated_at)} />
                  </dl>
                </CardContent>
              </Card>

              <div className="space-y-4 lg:col-span-2">
                {/* Pin de emergencia */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between gap-2 text-sm font-semibold">
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-red-500" /> Pin de emergencia
                      </span>
                      {site.lat && site.lng && (
                        <a
                          href={`https://www.google.com/maps?q=${site.lat},${site.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-normal text-blue-600 hover:underline"
                        >
                          <Navigation className="h-3 w-3" /> Google Maps
                        </a>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {site.lat && site.lng ? (
                      <>
                        <div className="overflow-hidden rounded-lg border border-border" style={{ height: 220 }}>
                          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                            <Map
                              defaultCenter={{ lat: site.lat, lng: site.lng }}
                              defaultZoom={16}
                              gestureHandling="none"
                              disableDefaultUI
                              mapId="site-view"
                              style={{ width: '100%', height: '100%' }}
                            >
                              <AdvancedMarker position={{ lat: site.lat, lng: site.lng }} />
                            </Map>
                          </APIProvider>
                        </div>
                        <p className="mt-2 font-mono text-xs text-muted-foreground">
                          {site.lat.toFixed(6)}, {site.lng.toFixed(6)}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm italic text-muted-foreground">
                        Sin pin. Fíjalo con <strong>Editar</strong> para poder navegar al sitio en emergencias.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Recordatorio de armar el panel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between gap-2 text-sm font-semibold">
                      Recordatorio de armar el panel
                      <Badge
                        variant="outline"
                        className={cn('text-xs font-normal', (site.arm_reminder_enabled ?? true)
                          ? 'border-green-500/30 bg-green-500/10 text-green-600'
                          : 'text-muted-foreground')}
                      >
                        {(site.arm_reminder_enabled ?? true) ? 'Activado' : 'Desactivado'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(site.arm_reminder_enabled ?? true) ? (
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <InfoField
                          label="Horario"
                          value={`${(site.arm_reminder_start ?? '06:00:00').slice(0, 5)} – ${(site.arm_reminder_end ?? '22:00:00').slice(0, 5)}`}
                        />
                        <InfoField label="Radio" value={`${site.arm_reminder_radius_m ?? 150} m`} />
                        <InfoField label="Mensaje" value={site.arm_reminder_message ?? 'Mensaje predeterminado de la app'} wide />
                      </dl>
                    ) : (
                      <p className="text-sm italic text-muted-foreground">
                        La app no avisará al salir del sitio con la alarma desarmada.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Barra de acciones: siempre visible para guardar sin bajar */}
              <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-background/95 px-4 py-2.5 shadow-sm backdrop-blur">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Pencil className="h-4 w-4" /> Editando datos del sitio
                </span>
                <div className="flex items-center gap-2">
                  {siteError && (
                    <span className="max-w-xs truncate text-xs text-red-500" title={siteError}>{siteError}</span>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setEditingSite(false)}>Cancelar</Button>
                  <Button size="sm" onClick={handleSaveSite} disabled={savingSite}>
                    {savingSite ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
                    Guardar
                  </Button>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-5">
                {/* Información del sitio */}
                <Card className="lg:col-span-3">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Información del sitio</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Nombre</label>
                        <Input value={siteForm.name ?? ''} onChange={(e) => setSiteForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Tipo</label>
                        <Input value={siteForm.tipo ?? ''} onChange={(e) => setSiteForm((f) => ({ ...f, tipo: e.target.value }))} className="mt-1" placeholder="Ej: Comercio / Paga" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-muted-foreground">Dirección</label>
                        <Input value={siteForm.address ?? ''} onChange={(e) => setSiteForm((f) => ({ ...f, address: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Departamento</label>
                        <Select
                          value={siteForm.department_id?.toString() ?? ''}
                          onValueChange={(v) => setSiteForm((f) => ({ ...f, department_id: Number(v), municipality_id: null }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {departments.map((d) => (
                              <SelectItem key={d.id} value={d.id.toString()}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Municipio</label>
                        <Select
                          value={siteForm.municipality_id?.toString() ?? ''}
                          onValueChange={(v) => setSiteForm((f) => ({ ...f, municipality_id: Number(v) }))}
                          disabled={!siteForm.department_id}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder={siteForm.department_id ? 'Seleccionar...' : 'Elija depto primero'} />
                          </SelectTrigger>
                          <SelectContent>
                            {municipalities.map((m) => (
                              <SelectItem key={m.id} value={m.id.toString()}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Clave verbal</label>
                        <Input value={siteForm.clave ?? ''} onChange={(e) => setSiteForm((f) => ({ ...f, clave: e.target.value }))} className="mt-1" placeholder="Ej: SHALOM" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Ubicación (referencia)</label>
                        <Input value={siteForm.ubicacion ?? ''} onChange={(e) => setSiteForm((f) => ({ ...f, ubicacion: e.target.value }))} className="mt-1" placeholder="Ej: Bodega trasera sobre la 5ta avenida" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-muted-foreground">Notas</label>
                        <textarea
                          rows={3}
                          className="mt-1 w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                          value={siteForm.notes ?? ''}
                          onChange={(e) => setSiteForm((f) => ({ ...f, notes: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Modo de cámaras */}
                    <div className="flex items-start gap-3 border-t border-border pt-4">
                      <button
                        type="button"
                        onClick={() => setSiteForm((f) => ({ ...f, camera_mode: (f as any).camera_mode === 'livestream' ? 'snapshot' : 'livestream' } as any))}
                        className={`relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                          (siteForm as any).camera_mode === 'livestream' ? 'bg-primary' : 'bg-input'
                        }`}
                        aria-label="Cambiar modo de cámaras"
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          (siteForm as any).camera_mode === 'livestream' ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                      <div>
                        <p className="text-sm font-medium">
                          Miniaturas de cámaras: {(siteForm as any).camera_mode === 'livestream' ? '🟢 video en vivo' : '📷 snapshot cada 5s'}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {(siteForm as any).camera_mode === 'livestream'
                            ? 'Video en tiempo real en las miniaturas. Usa más datos.'
                            : 'Una foto cada 5 segundos en las miniaturas. Usa menos datos.'}
                          {' '}La vista completa siempre es en vivo.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-4 lg:col-span-2">
                  {/* Pin de emergencia */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between gap-2 text-sm font-semibold">
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-4 w-4 text-red-500" /> Pin de emergencia
                        </span>
                        {siteForm.lat && siteForm.lng && (
                          <button
                            type="button"
                            onClick={() => setSiteForm((f) => ({ ...f, lat: null, lng: null }))}
                            className="text-xs font-normal text-destructive hover:underline"
                          >
                            Quitar pin
                          </button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-2 text-xs text-muted-foreground">
                        Busca la dirección o haz clic en el mapa para fijar el pin.
                      </p>
                      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <PlaceSearchInput
                          mapId="site-edit"
                          onPlaceSelect={(lat, lng) => setSiteForm((f) => ({ ...f, lat, lng }))}
                        />
                        <div className="overflow-hidden rounded-lg border border-border" style={{ height: 220 }}>
                          <Map
                            defaultCenter={siteForm.lat && siteForm.lng
                              ? { lat: siteForm.lat, lng: siteForm.lng }
                              : GT_CENTER}
                            defaultZoom={siteForm.lat ? 16 : 10}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                            mapId="site-edit"
                            style={{ width: '100%', height: '100%' }}
                            onClick={(e: { detail: { latLng?: { lat: number; lng: number } | null } }) => {
                              if (e.detail.latLng) {
                                setSiteForm((f) => ({
                                  ...f,
                                  lat: e.detail.latLng!.lat,
                                  lng: e.detail.latLng!.lng,
                                }));
                              }
                            }}
                          >
                            {siteForm.lat && siteForm.lng && (
                              <AdvancedMarker position={{ lat: siteForm.lat, lng: siteForm.lng }} />
                            )}
                          </Map>
                        </div>
                      </APIProvider>
                      {siteForm.lat && siteForm.lng && (
                        <p className="mt-2 font-mono text-xs text-muted-foreground">
                          {siteForm.lat.toFixed(6)}, {siteForm.lng.toFixed(6)}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recordatorio de armar el panel */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between gap-2 text-sm font-semibold">
                        Recordatorio de armar el panel
                        <label className="flex shrink-0 items-center gap-2 text-xs font-normal">
                          <input
                            type="checkbox"
                            checked={siteForm.arm_reminder_enabled ?? true}
                            onChange={(e) => setSiteForm((f) => ({ ...f, arm_reminder_enabled: e.target.checked }))}
                            className="h-4 w-4 rounded border-border"
                          />
                          Activado
                        </label>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-xs text-muted-foreground">
                        Aviso en la app al salir del sitio con la alarma desarmada.
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Inicio</label>
                          <Input
                            type="time"
                            value={(siteForm.arm_reminder_start ?? '06:00:00').slice(0, 5)}
                            onChange={(e) => setSiteForm((f) => ({ ...f, arm_reminder_start: `${e.target.value}:00` }))}
                            className="mt-1"
                            disabled={!(siteForm.arm_reminder_enabled ?? true)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Fin</label>
                          <Input
                            type="time"
                            value={(siteForm.arm_reminder_end ?? '22:00:00').slice(0, 5)}
                            onChange={(e) => setSiteForm((f) => ({ ...f, arm_reminder_end: `${e.target.value}:00` }))}
                            className="mt-1"
                            disabled={!(siteForm.arm_reminder_enabled ?? true)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Radio (m)</label>
                          <Input
                            type="number"
                            min={50}
                            max={2000}
                            step={10}
                            value={siteForm.arm_reminder_radius_m ?? 150}
                            onChange={(e) => setSiteForm((f) => ({ ...f, arm_reminder_radius_m: Number(e.target.value) }))}
                            className="mt-1"
                            disabled={!(siteForm.arm_reminder_enabled ?? true)}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Mensaje personalizado (opcional)</label>
                        <textarea
                          rows={2}
                          value={siteForm.arm_reminder_message ?? ''}
                          placeholder="Vacío = mensaje predeterminado de la app"
                          onChange={(e) => setSiteForm((f) => ({ ...f, arm_reminder_message: e.target.value || null }))}
                          className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                          disabled={!(siteForm.arm_reminder_enabled ?? true)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Gateway ── */}
        <TabsContent value="gateway" className="mt-4">
          <GatewayTab siteId={id!} siteName={site.name} accountName={site.account?.name ?? ''} />
        </TabsContent>
      </Tabs>

      {/* Edit zone modal (Zonificación) */}
      {editingZone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-foreground">Editar zona {editingZone.zone_number}</h3>
            <div>
              <label className="text-sm font-medium text-foreground">Nombre / dispositivo</label>
              <Input
                placeholder="Ej: Botón Pánico Recepción"
                value={zoneForm.name}
                onChange={(e) => setZoneForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Tipo</label>
              <Select value={zoneForm.type} onValueChange={(v) => setZoneForm((f) => ({ ...f, type: v as AlarmZoneType }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(ZONE_TYPE_CONFIG) as AlarmZoneType[]).map((t) => (
                    <SelectItem key={t} value={t}>{ZONE_TYPE_CONFIG[t].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                El tipo se infiere automáticamente del código CID recibido. Corrígelo aquí si el panel
                está reportando este dispositivo con un código equivocado (p. ej. un botón de pánico
                configurado como zona térmica en Hikvision).
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditingZone(null)}>Cancelar</Button>
              <Button className="flex-1" disabled={!zoneForm.name.trim() || savingZone} onClick={handleSaveZone}>
                {savingZone ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Contact modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-foreground">
              {editingContact ? 'Editar contacto' : 'Nuevo contacto'}
            </h3>
            <div>
              <label className="text-sm font-medium text-foreground">Nombre *</label>
              <Input
                required
                placeholder="Ej: Ana García"
                value={contactForm.name}
                onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Teléfono *</label>
              <Input
                required
                placeholder="+502 1234-5678"
                value={contactForm.phone}
                onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Tipo</label>
              <div className="flex gap-2 mt-1">
                {(Object.keys(CONTACT_TYPE_CONFIG) as ContactType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setContactForm((f) => ({ ...f, contact_type: t }))}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                      contactForm.contact_type === t
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {CONTACT_TYPE_CONFIG[t].icon}
                    {CONTACT_TYPE_CONFIG[t].label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Nota</label>
              <Input
                placeholder="Ej: LLAMAR SIEMPRE DE PRIMERO"
                value={contactForm.note}
                onChange={(e) => setContactForm((f) => ({ ...f, note: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowContactModal(false)}>Cancelar</Button>
              <Button
                className="flex-1"
                disabled={!contactForm.name || !contactForm.phone || savingContact}
                onClick={handleSaveContact}
              >
                {savingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign panel modal ── */}
      {showAssignPanel && (() => {
        const q = panelSearch.toLowerCase();
        const filtered = availablePanels.filter((p) =>
          p.name.toLowerCase().includes(q) ||
          p.serial_number.toLowerCase().includes(q) ||
          (p.model ?? '').toLowerCase().includes(q) ||
          p.status.toLowerCase().includes(q)
        );
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border shrink-0">
                <div>
                  <h3 className="font-semibold text-foreground text-base">Vincular panel de alarma</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {filtered.length} de {availablePanels.length} panel{availablePanels.length !== 1 ? 'es' : ''} sin cuenta
                  </p>
                </div>
                <button onClick={() => { setShowAssignPanel(false); setPanelSearch(''); }} className="p-1.5 rounded-lg hover:bg-muted">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Search */}
              {!loadingAssign && availablePanels.length > 0 && (
                <div className="px-6 py-3 border-b border-border shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Buscar por nombre, serie, modelo o estado…"
                      value={panelSearch}
                      onChange={(e) => setPanelSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              )}

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                {loadingAssign ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : availablePanels.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-10">No hay paneles disponibles sin cuenta asignada.</p>
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-10">Sin resultados para &ldquo;{panelSearch}&rdquo;</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background border-b border-border">
                      <tr>
                        <th className="text-left px-6 py-2.5 text-xs font-medium text-muted-foreground">Panel</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Serie</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Modelo</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Estado</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr key={p.id} className="border-b border-border/50 hover:bg-muted/40 transition-colors">
                          <td className="px-6 py-3 font-medium text-foreground">{p.name}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.serial_number}</td>
                          <td className="px-4 py-3 text-muted-foreground">{p.model ?? '—'}</td>
                          <td className="px-4 py-3">
                            <Badge className={cn('text-xs', p.status === 'ONLINE' ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-500')}>
                              {p.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleAssignPanel(p.id)}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                            >
                              Vincular
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Assign camera modal ── */}
      {showAssignCamera && (() => {
        const filtered = availableCameras.filter((c) =>
          c.name.toLowerCase().includes(cameraSearch.toLowerCase()) ||
          (c.device_manufacturer ?? '').toLowerCase().includes(cameraSearch.toLowerCase()) ||
          (c.device_model ?? '').toLowerCase().includes(cameraSearch.toLowerCase())
        );
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-background rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border shrink-0">
                <div>
                  <h3 className="font-semibold text-foreground text-base">Asignar cámara al sitio</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {availableCameras.length} disponible{availableCameras.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button onClick={() => { setShowAssignCamera(false); setCameraSearch(''); }} className="p-1.5 rounded-lg hover:bg-muted">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Search */}
              {availableCameras.length > 0 && (
                <div className="px-6 py-3 border-b border-border shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, fabricante o modelo…"
                      value={cameraSearch}
                      onChange={(e) => setCameraSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm bg-muted rounded-lg border-0 outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              )}

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4">
                {loadingAssign ? (
                  <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : availableCameras.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-10">No hay cámaras disponibles sin sitio asignado.</p>
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-10">Sin resultados para &ldquo;{cameraSearch}&rdquo;.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((cam) => (
                      <CameraPickerRow
                        key={cam.id}
                        cam={cam}
                        onSelect={() => { handleAssignCamera(cam.id); setCameraSearch(''); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Disable device modal ── */}
      {disableTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                <PowerOff className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Desactivar dispositivo</h3>
                <p className="text-xs text-muted-foreground">{disableTarget.name}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              El dispositivo quedará marcado como desactivado y se registrará en el historial de bajas.
              No se elimina ni se desvincula del sitio.
            </p>
            <div>
              <label className="text-sm font-medium text-foreground">Motivo (opcional)</label>
              <input
                type="text"
                placeholder="Ej: Cliente no pagó servicio"
                value={disableReason}
                onChange={(e) => setDisableReason(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDisableTarget(null)}>Cancelar</Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={disabling}
                onClick={handleConfirmDisable}
              >
                {disabling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PowerOff className="w-4 h-4 mr-1" />}
                Desactivar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR modal ── */}
      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-xs p-6 space-y-4 text-center">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Código QR de cámara</h3>
              <button onClick={() => setQrModal(null)} className="p-1 rounded hover:bg-muted">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">{qrModal.name}</p>
            <div className="flex justify-center p-4 bg-white rounded-xl">
              <QRCodeSVG value={qrModal.code} size={180} />
            </div>
            <p className="font-mono text-lg font-bold tracking-widest text-foreground">{qrModal.code}</p>
            <p className="text-xs text-muted-foreground">
              El cliente escanea este código desde la app ASI Security para agregar la cámara a su cuenta.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Site camera card (assigned cameras with snapshot) ────────

function SiteCameraCard({
  cam,
  onQR,
  onUnlink,
  onDisable,
  onEnable,
  onRenamed,
}: {
  cam: any;
  onQR: () => void;
  onUnlink: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onRenamed?: (newName: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isOnline = cam.status === 'ONLINE' && !cam.is_disabled;

  // Inline rename state
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(cam.name as string);
  const [savingName, setSavingName] = useState(false);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === cam.name) { setEditingName(false); setNameInput(cam.name); return; }
    setSavingName(true);
    try {
      await updateCameraName(cam.id, trimmed);
      onRenamed?.(trimmed);
      setEditingName(false);
    } catch {
      setNameInput(cam.name);
      setEditingName(false);
    } finally {
      setSavingName(false);
    }
  };

  // ── go2rtc live video (ONVIF/gateway cameras) ──
  // Use the sub-stream for the card (lower res, faster connect).
  const isGo2rtc = !!cam.go2rtc_stream_id;
  const go2rtcStreamId = isGo2rtc && isOnline ? `${cam.go2rtc_stream_id}_sub` : null;
  const go2rtc = useGo2rtcPlayer(videoRef, go2rtcStreamId);

  // Lightweight snapshot poll while WebRTC connects (or as fallback for HCT).
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotTick, setSnapshotTick] = useState(0);

  // Refresh snapshot every 4s while go2rtc isn't fully connected.
  useEffect(() => {
    if (!isOnline) return;
    if (isGo2rtc && go2rtc.state === 'connected') return; // live video active, no snapshot needed
    const t = setInterval(() => setSnapshotTick((n) => n + 1), 4000);
    return () => clearInterval(t);
  }, [isOnline, isGo2rtc, go2rtc.state]);

  useEffect(() => {
    if (!isOnline) { setSnapshotUrl(null); return; }

    // go2rtc snapshot endpoint — cache-busted so each tick yields a fresh frame.
    if (isGo2rtc) {
      const go2rtcBase = (import.meta.env.VITE_GO2RTC_URL as string | undefined) || 'https://cameras.corporacionasi.com';
      setSnapshotUrl(`${go2rtcBase}/go2rtc/api/frame.jpeg?src=${encodeURIComponent(cam.go2rtc_stream_id)}&_t=${Date.now()}`);
      return;
    }

    // HCT cameras: refresh via cameras-proxy snapshot.
    const params = getSnapshotParamsForCamera(cam);
    if (!params) return;
    let cancelled = false;
    getSnapshot(params.serial, params.channelNo)
      .then(({ url }) => { if (!cancelled) setSnapshotUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cam.id, isOnline, isGo2rtc, snapshotTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const showLiveVideo = isGo2rtc && go2rtc.state === 'connected';
  const showError = isGo2rtc && go2rtc.state === 'failed' && !snapshotUrl;

  return (
    <div className={cn('rounded-xl border border-border bg-card overflow-hidden', cam.is_disabled && 'opacity-60')}>
      {/* Live video / snapshot stack */}
      <div className="w-full aspect-video bg-muted relative flex items-center justify-center overflow-hidden">
        {/* Persistent snapshot layer (visible until live WebRTC connects) */}
        {snapshotUrl && (
          <img
            src={snapshotUrl}
            alt={cam.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* go2rtc live video (fades in once connected) */}
        {isGo2rtc && isOnline && (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={cn(
              'absolute inset-0 w-full h-full object-cover transition-opacity duration-500',
              showLiveVideo ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}
        {/* Empty / loading / error states */}
        {!snapshotUrl && !showLiveVideo && (
          <div className="flex flex-col items-center gap-1 opacity-40">
            {showError
              ? <VideoOff className="w-6 h-6 text-amber-500" />
              : <Video className="w-6 h-6 text-muted-foreground" />
            }
            {isOnline && !showError && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
        )}
        <Badge className={cn(
          'absolute top-2 left-2 text-xs shadow',
          cam.is_disabled ? 'bg-red-500/90 text-white' :
          cam.status === 'ONLINE' ? 'bg-green-500/90 text-white' : 'bg-gray-500/80 text-white'
        )}>
          {cam.is_disabled ? 'Desactivado' : cam.status}
        </Badge>
        {/* Subtle live indicator when WebRTC stream is active */}
        {showLiveVideo && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/50 text-white text-[10px] font-medium">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            EN VIVO
          </div>
        )}
      </div>
      {/* Name + actions */}
      <div className="px-3 py-2 flex items-center gap-1">
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNameInput(cam.name); } }}
                className="h-7 text-sm font-medium"
                autoFocus
              />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={handleSaveName} disabled={savingName}>
                {savingName ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingName(false); setNameInput(cam.name); }}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <p className="font-medium text-sm text-foreground truncate">{nameInput}</p>
          )}
          <p className="text-xs text-muted-foreground truncate">
            {cam.device_manufacturer ? `${cam.device_manufacturer} ${cam.device_model ?? ''}` : cam.protocol}
          </p>
        </div>
        {!editingName && (
          <Button variant="ghost" size="sm" title="Renombrar cámara" onClick={() => { setNameInput(nameInput); setEditingName(true); }}>
            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        )}
        <Button variant="ghost" size="sm" title="Ver código QR" onClick={onQR}>
          <QrCode className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
        <PermissionGate require="cameras.view">
          {cam.is_disabled ? (
            <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700" title="Reactivar cámara" onClick={onEnable}>
              <Power className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="text-orange-500 hover:text-orange-600" title="Desactivar cámara" onClick={onDisable}>
              <PowerOff className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" title="Desvincular cámara" onClick={onUnlink}>
            <Unlink className="w-3.5 h-3.5" />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}

// ─── Camera picker row with live snapshot ───────────────────

function getSnapshotParamsForCamera(cam: Camera): { serial: string; channelNo: number } | null {
  if (cam.protocol !== 'HIKVISION_HCT' || !cam.hikvision_device_id) return null;
  if (cam.hikvision_device_id.includes(':')) {
    const [serial, ch] = cam.hikvision_device_id.split(':');
    return { serial, channelNo: Number(ch) };
  }
  return { serial: cam.hikvision_device_id, channelNo: 1 };
}

function CameraPickerRow({ cam, onSelect }: { cam: Camera; onSelect: () => void }) {
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    if (cam.status !== 'ONLINE') return;
    const params = getSnapshotParamsForCamera(cam);
    if (!params) return;
    let cancelled = false;
    getSnapshot(params.serial, params.channelNo)
      .then(({ url }) => { if (!cancelled) setThumb(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cam.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <button
      onClick={onSelect}
      className="group w-full rounded-xl border border-border hover:border-primary hover:shadow-md text-left transition-all overflow-hidden bg-card"
    >
      {/* Snapshot preview */}
      <div className="w-full aspect-video bg-muted relative flex items-center justify-center overflow-hidden">
        {thumb
          ? <img src={thumb} alt={cam.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          : (
            <div className="flex flex-col items-center gap-1 opacity-40">
              <Video className="w-6 h-6 text-muted-foreground" />
              {cam.status === 'ONLINE' && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            </div>
          )}
        <Badge className={cn(
          'absolute top-2 right-2 text-xs shadow',
          cam.status === 'ONLINE' ? 'bg-green-500/90 text-white' : 'bg-gray-500/80 text-white'
        )}>
          {cam.status}
        </Badge>
      </div>
      {/* Name + meta */}
      <div className="px-3 py-2.5">
        <p className="font-medium text-sm text-foreground truncate">{cam.name}</p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {cam.device_manufacturer ? `${cam.device_manufacturer} ${cam.device_model ?? ''}` : cam.protocol}
        </p>
      </div>
    </button>
  );
}

/** Label/value pair for the Datos tab. Empty values render as "—" so missing data is visible. */
function InfoField({ label, value, wide, mono, multiline }: {
  label: string;
  value: ReactNode;
  wide?: boolean;
  mono?: boolean;
  multiline?: boolean;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2')}>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn(
        'mt-0.5 break-words text-sm',
        empty ? 'text-muted-foreground/60' : 'text-foreground',
        mono && !empty && 'font-mono',
        multiline && 'whitespace-pre-line',
      )}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' });
}
